import api from './api';

export type EmployeeKind = 'user' | 'external';

/** UoP, B2B, umowa zlecenie — wartości zapisu w API/DB */
export type KadryContractType = 'uop' | 'b2b' | 'uz';

/** Filtr zakładek: praca w terenie/produkcja vs biuro */
export type KadryWorkMode = 'field' | 'office';

export interface KadryEmployee {
  kind: EmployeeKind;
  id: string | number;
  user_id?: string | null;

  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  full_name: string;
  email: string | null;
  phone: string | null;
  position: string | null;
  avatar_url: string | null;
  is_active: boolean;
  role: string | null;

  pesel?: string | null;
  hire_date: string | null;
  termination_date: string | null;
  kadry_notes: string | null;

  leave_days_entitlement: number | null;
  /** Bilans ręczny (np. wdrożenie / dni spoza systemu) — edytowalny w Kadry */
  leave_days_used: number | null;
  /** Z wniosków zatwierdzonych w bieżącym roku (urlop wyp./na żądanie) — tylko odczyt z API */
  leave_annual_approved_from_requests?: number | null;
  /** Suma: ręczne + wnioski — uzupełniane w GET/PUT użytkownika */
  leave_days_used_total?: number | null;
  leave_days_remaining: number | null;

  medical_exam_last_date: string | null;
  medical_exam_valid_until: string | null;

  /** UoP / B2B / uz — opcjonalne do czasu migracji API */
  contract_type?: string | null;
  contract_is_fixed_term?: boolean | null;
  contract_end_date?: string | null;
  bhp_general_valid_until?: string | null;
  bhp_position_valid_until?: string | null;
  kadry_work_mode?: string | null;

  google_drive_user_folder_link: string | null;

  created_at?: string;
  updated_at?: string;
}

export interface KadryEmployeeUpdatePayload {
  // wspólne
  leave_days_entitlement?: number | null;
  leave_days_used?: number | null;
  medical_exam_last_date?: string | null;
  medical_exam_valid_until?: string | null;
  hire_date?: string | null;
  termination_date?: string | null;
  kadry_notes?: string | null;
  position?: string | null;
  contract_type?: string | null;
  contract_is_fixed_term?: boolean | null;
  contract_end_date?: string | null;
  bhp_general_valid_until?: string | null;
  bhp_position_valid_until?: string | null;
  kadry_work_mode?: string | null;

  // tylko user
  telefon?: string | null;

  // tylko external
  first_name?: string;
  last_name?: string;
  display_name?: string | null;
  email?: string | null;
  phone?: string | null;
  pesel?: string | null;
  is_active?: boolean;
  notes?: string | null;
}

export interface ExternalEmployeeCreatePayload {
  first_name: string;
  last_name: string;
  display_name?: string | null;
  email?: string | null;
  phone?: string | null;
  position?: string | null;
  pesel?: string | null;
  hire_date?: string | null;
  termination_date?: string | null;
  is_active?: boolean;
  leave_days_entitlement?: number | null;
  leave_days_used?: number | null;
  medical_exam_last_date?: string | null;
  medical_exam_valid_until?: string | null;
  contract_type?: string | null;
  contract_is_fixed_term?: boolean | null;
  contract_end_date?: string | null;
  bhp_general_valid_until?: string | null;
  bhp_position_valid_until?: string | null;
  kadry_work_mode?: string | null;
  notes?: string | null;
}

export interface KadryLeaveRequest {
  id: number;
  request_id: string;
  request_date: string;
  applicant_user_id: string;
  applicant_full_name: string;
  title: string;
  details: string | null;
  status: 'złożony' | 'zgoda' | 'odrzucony';
  generated_pdf_url: string | null;
  date_from: string;
  date_to: string;
  days: number;
  created_at?: string;
}

export interface KadryDocument {
  id: number;
  owner_kind: EmployeeKind;
  owner_id: string;
  title: string;
  doc_type: string | null;
  url: string;
  notes: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export async function listEmployees(): Promise<KadryEmployee[]> {
  const { data } = await api.get('/kadry/employees');
  return (data?.data ?? []) as KadryEmployee[];
}

export async function getEmployee(kind: EmployeeKind, id: string | number): Promise<KadryEmployee> {
  const { data } = await api.get(`/kadry/employees/${kind}/${encodeURIComponent(String(id))}`);
  return data?.data as KadryEmployee;
}

export async function updateEmployee(kind: EmployeeKind, id: string | number, payload: KadryEmployeeUpdatePayload): Promise<KadryEmployee> {
  const { data } = await api.put(`/kadry/employees/${kind}/${encodeURIComponent(String(id))}`, payload);
  return data?.data as KadryEmployee;
}

export async function createExternalEmployee(payload: ExternalEmployeeCreatePayload): Promise<KadryEmployee> {
  const { data } = await api.post('/kadry/external-employees', payload);
  return data?.data as KadryEmployee;
}

export async function deleteExternalEmployee(id: string | number): Promise<{ success: boolean }>{
  const { data } = await api.delete(`/kadry/external-employees/${encodeURIComponent(String(id))}`);
  return data;
}

export async function listEmployeeLeaveRequests(kind: EmployeeKind, id: string | number): Promise<KadryLeaveRequest[]> {
  const { data } = await api.get(`/kadry/employees/${kind}/${encodeURIComponent(String(id))}/leave-requests`);
  return (data?.data ?? []) as KadryLeaveRequest[];
}

export async function listEmployeeDocuments(kind: EmployeeKind, id: string | number): Promise<KadryDocument[]> {
  const { data } = await api.get(`/kadry/employees/${kind}/${encodeURIComponent(String(id))}/documents`);
  return (data?.data ?? []) as KadryDocument[];
}

export async function createEmployeeDocument(kind: EmployeeKind, id: string | number, payload: { title: string; url: string; doc_type?: string | null; notes?: string | null }): Promise<KadryDocument> {
  const { data } = await api.post(`/kadry/employees/${kind}/${encodeURIComponent(String(id))}/documents`, payload);
  return data?.data as KadryDocument;
}

export async function deleteEmployeeDocument(docId: number | string): Promise<{ success: boolean }>{
  const { data } = await api.delete(`/kadry/documents/${encodeURIComponent(String(docId))}`);
  return data;
}

/**
 * Wgranie zdjęcia pracownika (multipart/form-data, pole `file`).
 * Backend zapisuje plik w uploads/kadry i ustawia `avatar_url`.
 */
export async function uploadEmployeePhoto(kind: EmployeeKind, id: string | number, file: File): Promise<KadryEmployee> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post(
    `/kadry/employees/${kind}/${encodeURIComponent(String(id))}/photo`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data?.data as KadryEmployee;
}

export async function deleteEmployeePhoto(kind: EmployeeKind, id: string | number): Promise<KadryEmployee> {
  const { data } = await api.delete(`/kadry/employees/${kind}/${encodeURIComponent(String(id))}/photo`);
  return data?.data as KadryEmployee;
}
